import React from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { useOnlineStatus, ONLINE, OFFLINE, BACK_ONLINE } from '../../util/hooks/useOnlineStatus';
import Icon from '../Icon/Icon';

import css from './OfflineBanner.module.css';

/**
 * The offline banner from the Components board, across the top of every page: "You're offline.
 * We'll reconnect as soon as you're back." while the browser has no network, then a tick and
 * "Back online" for a moment. The app shell renders it once; it follows the browser's connection
 * (`useOnlineStatus`). `status` fixes the state, for the Styleguide.
 *
 * The box is always in the page, empty while online, so screen readers announce the change.
 */
const OfflineBanner = props => {
  const { rootClassName, className, status: fixedStatus } = props;
  const liveStatus = useOnlineStatus();
  const status = fixedStatus || liveStatus;
  const classes = classNames(rootClassName || css.root, className, {
    [css.offline]: status === OFFLINE,
    [css.backOnline]: status === BACK_ONLINE,
  });

  return (
    <div className={classes} role="status">
      {status === OFFLINE ? (
        <>
          <span className={css.dot} aria-hidden="true" />
          <FormattedMessage id="OfflineBanner.offline" />
        </>
      ) : null}
      {status === BACK_ONLINE ? (
        <>
          <Icon name="check" size={16} />
          <FormattedMessage id="OfflineBanner.backOnline" />
        </>
      ) : null}
    </div>
  );
};

OfflineBanner.defaultProps = {
  rootClassName: null,
  className: null,
  status: null,
};

OfflineBanner.propTypes = {
  rootClassName: string,
  className: string,
  status: oneOf([ONLINE, OFFLINE, BACK_ONLINE]),
};

export default OfflineBanner;
